import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Coffee, UtensilsCrossed, Wine, Star, Check, ChevronDown } from 'lucide-react';
import { PublicLayout, ScrollReveal, SectionTitle, PriceTag, Badge } from './components';
import { SITE_CONFIG, IMAGES, SERVICES, CLIENTS, BLOG_POSTS, FAQS } from './config';
import { SEOHead } from './SEOHead';

// ============================================
// HERO SECTION
// ============================================
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img 
          src={IMAGES.hero}
          alt="Catering para empresas en Madrid - Duetto Catering"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
        <ScrollReveal delay={200}>
          <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium border border-white/20 mb-6">
            Desde 2018 sirviendo a empresas en Madrid
          </span>
        </ScrollReveal>

        <ScrollReveal delay={400}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white tracking-tight mb-6">
            Catering para
            <span className="block text-emerald-400">empresas en Madrid</span>
          </h1>
        </ScrollReveal>

        <ScrollReveal delay={600}>
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
            Desayunos, lunch y cócteles para eventos corporativos. 
            Servicio profesional, puntual y con la mejor relación calidad-precio.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={800}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/web/contacto"
              className="group w-full sm:w-auto px-8 py-4 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-all duration-300 hover:shadow-xl flex items-center justify-center gap-2"
            >
              Pedir presupuesto gratis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`}
              className="w-full sm:w-auto px-8 py-4 border-2 border-white/40 text-white font-medium rounded-full hover:bg-white hover:text-gray-900 transition-all duration-300 flex items-center justify-center gap-2"
            >
              Llamar ahora: {SITE_CONFIG.phone}
            </a>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={1000}>
          <div className="mt-12 flex items-center justify-center gap-6 text-white/70 text-sm">
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              Desde 6€/persona
            </span>
            <span className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              Transporte incluido Madrid
            </span>
          </div>
        </ScrollReveal>
      </div>

      {/* Scroll indicator */}
      <a 
        href="#servicios" 
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 hover:text-white transition-colors animate-bounce"
        aria-label="Ver servicios"
      >
        <ChevronDown size={32} />
      </a>
    </section>
  );
};

// ============================================
// SERVICIOS RESUMEN
// ============================================
const ServicesSection = () => {
  const services = [
    {
      icon: Coffee,
      ...SERVICES.desayunos,
      image: IMAGES.desayunos.main,
      link: '/web/desayunos',
    },
    {
      icon: UtensilsCrossed,
      ...SERVICES.lunch,
      image: IMAGES.lunch.main,
      link: '/web/lunch',
    },
    {
      icon: Wine,
      ...SERVICES.cocteles,
      image: IMAGES.lunch.buffet,
      link: '/web/cocteles',
    },
  ];

  return (
    <section id="servicios" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <SectionTitle 
            badge="Nuestros servicios"
            title="Soluciones de catering para cada ocasión"
            subtitle="Adaptamos cada propuesta a tu evento, presupuesto y número de asistentes."
          />
        </ScrollReveal>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ScrollReveal key={service.slug} delay={index * 100}>
              <Link 
                to={service.link}
                className="group block bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500"
              >
                {/* Imagen */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={`${service.title} - Duetto Catering Madrid`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {service.shortDescription}
                  </p>
                  <div className="flex items-center justify-between">
                    <PriceTag price={service.priceFrom} suffix="€/pers" />
                    <span className="text-emerald-600 font-medium text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                      Ver menús <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// CLIENTES
// ============================================
const ClientsSection = () => {
  return (
    <section className="py-12 bg-gray-50 border-y border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-sm font-medium text-gray-500 uppercase tracking-wider mb-8">
          Empresas que confían en nosotros
        </p>
        <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-4">
          {CLIENTS.map((client) => (
            <span 
              key={client}
              className="text-gray-400 hover:text-emerald-600 transition-colors font-medium"
            >
              {client}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// POR QUÉ ELEGIRNOS
// ============================================
const WhyUsSection = () => {
  const reasons = [
    {
      title: 'Precios transparentes',
      description: 'Sin sorpresas. Sabes lo que pagas desde el primer momento.',
    },
    {
      title: 'Puntualidad garantizada',
      description: 'Llegamos siempre a tiempo. Tu evento no puede esperar.',
    },
    {
      title: 'Transporte incluido',
      description: 'Entrega gratuita en Madrid capital y zona Noroeste.',
    },
    {
      title: 'Atención personalizada',
      description: 'Un asesor te ayuda a elegir el menú perfecto para tu evento.',
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-emerald-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <SectionTitle 
            badge="Por qué elegirnos"
            title="Profesionalidad en cada detalle"
            light
          />
        </ScrollReveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {reasons.map((reason, index) => (
            <ScrollReveal key={reason.title} delay={index * 100}>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-emerald-800 flex items-center justify-center">
                  <Check className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{reason.title}</h3>
                <p className="text-emerald-200 text-sm">{reason.description}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-16 pt-16 border-t border-emerald-800 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { value: `${new Date().getFullYear() - SITE_CONFIG.foundedYear}+`, label: 'Años de experiencia' },
            { value: '500+', label: 'Eventos realizados' },
            { value: '40k+', label: 'Comensales atendidos' },
            { value: '4.9', label: 'Valoración media', icon: Star },
          ].map((stat, index) => (
            <ScrollReveal key={stat.label} delay={index * 100}>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <span className="text-4xl lg:text-5xl font-bold">{stat.value}</span>
                  {stat.icon && <stat.icon className="w-6 h-6 text-yellow-400 fill-yellow-400" />}
                </div>
                <p className="text-emerald-200 text-sm">{stat.label}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// BLOG PREVIEW
// ============================================
const BlogPreview = () => {
  const recentPosts = BLOG_POSTS.slice(0, 3);

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <SectionTitle 
            badge="Blog"
            title="Consejos para tu próximo evento"
            subtitle="Ideas, tendencias y recomendaciones del mundo del catering corporativo."
          />
        </ScrollReveal>

        <div className="grid md:grid-cols-3 gap-8">
          {recentPosts.map((post, index) => (
            <ScrollReveal key={post.id} delay={index * 100}>
              <Link 
                to={`/web/blog/${post.slug}`}
                className="group block bg-gray-50 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge>{post.category}</Badge>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{post.readTime} lectura</span>
                    <span>{new Date(post.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
                  </div>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={300}>
          <div className="text-center mt-10">
            <Link 
              to="/web/blog"
              className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 transition-colors"
            >
              Ver todos los artículos <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

// ============================================
// FAQ SECTION
// ============================================
const FAQSection = () => {
  const [openIndex, setOpenIndex] = React.useState(0);

  return (
    <section className="py-20 lg:py-28 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <SectionTitle 
            badge="Preguntas frecuentes"
            title="¿Tienes dudas?"
          />
        </ScrollReveal>

        <div className="space-y-4">
          {FAQS.map((faq, index) => (
            <ScrollReveal key={index} delay={index * 50}>
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-48' : 'max-h-0'
                }`}>
                  <p className="px-6 pb-4 text-gray-600">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// CTA FINAL
// ============================================
const CTASection = () => {
  return (
    <section className="py-20 bg-emerald-600">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <ScrollReveal>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            ¿Organizas un evento próximamente?
          </h2>
          <p className="text-xl text-emerald-100 mb-8">
            Cuéntanos qué necesitas y te enviamos un presupuesto sin compromiso.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/web/contacto"
              className="w-full sm:w-auto px-8 py-4 bg-white text-emerald-700 font-semibold rounded-full hover:bg-emerald-50 transition-colors flex items-center justify-center gap-2"
            >
              Solicitar presupuesto gratis
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`}
              className="w-full sm:w-auto px-8 py-4 border-2 border-white/50 text-white font-medium rounded-full hover:bg-white/10 transition-colors"
            >
              O llámanos: {SITE_CONFIG.phone}
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

// ============================================
// PÁGINA PRINCIPAL
// ============================================
const HomePage = () => {
  return (
    <PublicLayout>
      <SEOHead page="home" />
      <HeroSection />
      <ServicesSection />
      <ClientsSection />
      <WhyUsSection />
      <BlogPreview />
      <FAQSection />
      <CTASection />
    </PublicLayout>
  );
};

export default HomePage;
